import {UserModel} from 'app/modules/auth'
import clsx from 'clsx'
import {
  getActiveSession,
  hasPermission,
  isAdmin,
  isParent,
  isSelectiveAiDomains,
  isStaff,
  isStudent,
  isTeacher,
  shouldShowAiCredits,
} from 'common/HelperFunctions'
import Avatar from 'common/components/Avatar'
import FeeBetaToggle from 'common/components/BetaToggle/Fee'
import HomeworkBetaToggle from 'common/components/BetaToggle/Homework'
import HelpPopup from 'common/components/HelpPopup'
import {USER_TYPES_NUMERIC} from 'common/constants'
import {isHomeworkBetaEnabled, revertToOriginalUser} from 'common/helpers'
import {AiCreditType} from 'common/hooks/useAiCredits'
import {useMediaQuery} from 'common/hooks/useMediaQuery'
import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router'
import {RootState, UserReducer} from 'setup'
import {getUserStatistics} from 'setup/redux/Actions/Dashboard/dashboard'
import {getAccessToken} from 'setup/redux/Actions/Timetable/timetable'
import Swal from 'sweetalert2'

import {KTIcon} from '_metronic/helpers'
import BranchSwitcher from '_metronic/layout/components/header/BranchSwitcher'
import ChildSwitcher from '_metronic/layout/components/header/ChildSwitcher'
import SupportTicketsLink from '_metronic/layout/components/header/SupportTicketsLink'
import {getTheme} from '_metronic/partials'
import {ThemeModeSegment} from '_metronic/partials/layout/theme-mode/ThemeModeSegment'

import {Languages} from './Languages'

/** Bootstrap `lg`; keep in sync with `$grid-breakpoints.lg`. */
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)'

const HeaderUserMenu: FC = () => {
  const [planName, setPlanName] = useState<string | null>(null)

  // Fetch plan info when menu is opened
  const handleMenuOpen = () => {
    dispatch(
      getUserStatistics(
        (res: any) => {
          const plan = res?.result?.payload?.subscription_info?.plan_name || null
          setPlanName(plan)
        },
        () => setPlanName(null)
      )
    )
  }

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any
  const userReducer: UserReducer = useSelector<RootState>(
    ({userReducer}) => userReducer,
    shallowEqual
  ) as UserReducer

  const user: UserModel = userReducer.user as UserModel
  const userType: string = userReducer.userType as string

  const impersonation: any = userReducer.impersonation

  const aiCredits = useSelector<RootState>(
    ({userReducer}) => userReducer.aiCredits,
    shallowEqual
  ) as AiCreditType | null

  const intl = useIntl()
  const dispatch = useDispatch()
  const isSelectiveAiDomain = isSelectiveAiDomains()
  // Below lg the header has no room for the branch select, support link,
  // Help, Chat, Settings and theme switch, so they live here. Mounted only on
  // small viewports: BranchSwitcher fetches on mount and the desktop header
  // already has its own copy.
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)
  const activeSession = getActiveSession()
  const [isViewingAiCreditsUsage, setIsViewingAiCreditsUsage] = useState(false)

  const PLAN_SELECTION_PATH = '/PlanSelection'
  const handleGetAccessToken = () => {
    if (isViewingAiCreditsUsage) return
    setIsViewingAiCreditsUsage(true)
    dispatch(
      getAccessToken(
        (response: any) => {
          setIsViewingAiCreditsUsage(false)
          const {accessToken, urdd_id} = response.result || response
          if (accessToken && urdd_id) {
            const aiCreditsUrl = window.location.hostname.includes('da1timetable.info')
              ? 'https://dev-subscription.ilmversity.ai/ai-credits'
              : import.meta.env.VITE_APP_AI_CREDITS_URL
            if (aiCreditsUrl) {
              try {
                const sourceDomain = window.location.hostname
                const theme = getTheme()
                const returnTo = window.location.href
                const url = `${aiCreditsUrl.replace(/\/$/, '')}${PLAN_SELECTION_PATH}?accessToken=${encodeURIComponent(accessToken)}&urdd_id=${encodeURIComponent(urdd_id)}&sourceDomain=${encodeURIComponent(sourceDomain)}&theme=${encodeURIComponent(theme)}&returnTo=${encodeURIComponent(returnTo)}&intent=planSelection`
                window.location.href = url
              } catch (err) {
                console.error('Failed to open Plan Selection:', err)
              }
            }
          }
        },
        (error: any) => {
          setIsViewingAiCreditsUsage(false)
          console.error('Failed to get access token:', error)
        }
      )
    )
  }

  // Handler for Users/Roles click
  const handleRestrictedClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (planName === 'Freemium') {
      e.preventDefault()
      const result = await Swal.fire({
        icon: 'info',
        title: 'Upgrade Required',
        text: 'Upgrade your pack to use this feature.',
        showCancelButton: true,
        confirmButtonText: 'Upgrade Plan',
        cancelButtonText: 'Close',
      })
      if (result.isConfirmed) {
        handleGetAccessToken()
      }
    }
  }

  return (
    <div
      className={clsx(
        'menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold py-4 fs-6',
        isSelectiveAiDomain ? 'w-350px' : 'w-275px'
      )}
      data-kt-menu='true'
      onMouseEnter={handleMenuOpen}
    >
      <div className='menu-item px-3'>
        {impersonation && <div className='p-2'>{intl.$t({id: 'USER.LOGGED_IN_AS'})}</div>}
        <div className='menu-content d-flex align-items-center px-3'>
          <Avatar
            src={user.profilePicture}
            size='50'
            label={user.name}
            color='primary'
            circle={isSelectiveAiDomain}
            className={clsx('me-5', {'border border-primary': !!impersonation})}
          />

          <div className='d-flex flex-column text-break'>
            <div className='fw-bolder d-flex align-items-center fs-5'>
              {isSelectiveAiDomain && user.name?.startsWith('admin_')
                ? user.name.replace('admin_', '')
                : user.name}
              <span className='badge badge-light-success fw-bolder fs-8 px-2 py-1 ms-2'>
                {userType}
              </span>
            </div>
            <span className='fw-bold text-muted text-hover-primary fs-7 text-break cursor-pointer'>
              {user.email}
            </span>
          </div>
        </div>
      </div>

      <div className='separator my-2'></div>

      {!isDesktop && (
        <>
          {!isSelectiveAiDomain && (
            <>
              {[USER_TYPES_NUMERIC.PARENT].includes(user.type as number) && (
                <div className='menu-item px-5'>
                  <div className='menu-content px-5 py-2'>
                    <ChildSwitcher list={user.children} />
                  </div>
                </div>
              )}
              <div className='menu-item px-5'>
                <div className='menu-content px-5 py-2'>
                  <BranchSwitcher className='w-100' />
                </div>
              </div>
              {activeSession && activeSession?.db_postfix && (
                <div className='menu-item px-5'>
                  <div className='menu-content px-5 py-1'>
                    <span className='badge p-3 fs-7' style={{backgroundColor: 'var(--bs-teal)'}}>
                      Session: {activeSession.name}
                    </span>
                  </div>
                </div>
              )}
              <SupportTicketsLink variant='menu' />
              <HelpPopup variant='menu' />
              <div className='menu-item px-5'>
                <Link to='/messages' className='menu-link px-5'>
                  <span className='menu-icon'>
                    <KTIcon iconName='message-text-2' className='fs-2' />
                  </span>
                  <span className='menu-title'>{intl.formatMessage({id: 'MENU.MESSAGES'})}</span>
                </Link>
              </div>
              {hasPermission('user_manager_settings') && (
                <div className='menu-item px-5'>
                  <Link to='/institute/general-settings/settings' className='menu-link px-5'>
                    <span className='menu-icon'>
                      <i className='fa fa-gear fs-3' />
                    </span>
                    <span className='menu-title'>{intl.formatMessage({id: 'MENU.SETTINGS'})}</span>
                  </Link>
                </div>
              )}
              {isHomeworkBetaEnabled() && (
                <HomeworkBetaToggle className='menu-item px-5 py-1 menu-content' />
              )}
              <FeeBetaToggle className='menu-item px-5 py-1 menu-content' />
            </>
          )}
          <div className='menu-item px-5'>
            <div className='menu-content px-5 py-2'>
              <ThemeModeSegment className='w-100' />
            </div>
          </div>
          <div className='separator my-2'></div>
        </>
      )}

      {impersonation && (
        <>
          <div className='menu-item px-3'>
            <div className='d-flex align-items-center justify-content-between p-2'>
              <span>{intl.$t({id: 'USER.ORIGINAL_USER'})}</span>

              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-primary'
                onClick={revertToOriginalUser}
              >
                <i className='fa fa-undo'></i>
              </button>
            </div>
            <div className='menu-content d-flex align-items-center px-3'>
              <Avatar
                src={impersonation.user.profilePicture}
                size='50'
                label={impersonation.user.name}
                color='primary'
                className='me-5'
              />

              <div className='d-flex flex-column text-break'>
                <div className='fw-bolder d-flex align-items-center fs-5'>
                  {impersonation.user.name}
                  <span className='badge badge-light-success fw-bolder fs-8 px-2 py-1 ms-2'>
                    {impersonation.userType}
                  </span>
                </div>
                <span className='fw-bold text-muted text-hover-primary fs-7 text-break cursor-pointer'>
                  {impersonation.user.email}
                </span>
              </div>
            </div>
          </div>

          <div className='separator my-2'></div>
        </>
      )}

      {isSelectiveAiDomain && (
        <>
          <div className='menu-item px-5'>
            <Link to='/profile/info' className='menu-link px-5 d-flex align-items-center'>
              <i className='fa fa-user fs-6 me-4' />
              <span className='menu-text'>{intl.formatMessage({id: 'MENU.MY_PROFILE'})}</span>
            </Link>
          </div>
          {/* User Management for isSelectiveAiDomains */}
          {hasPermission('user_manager_staffs') && (
            <>
              <div className='separator my-2' />
              <div className='menu-item px-3'>
                <div className='menu-content d-flex align-items-center px-3'>
                  <span className='fw-bold text-muted fs-7'>
                    {intl.formatMessage({id: 'MENU.USER_MANAGEMENT'})}
                  </span>
                </div>
              </div>
              <div className='menu-item px-5'>
                <Link
                  to='/institute/staff'
                  className='menu-link px-5'
                  onClick={handleRestrictedClick}
                >
                  <span className='menu-icon'>
                    <i className='fas fa-user fs-6' />
                  </span>
                  <span className='menu-title'>
                    {intl.formatMessage({id: 'MENU.USER_MANAGEMENT_USERS'})}
                  </span>
                </Link>
              </div>
              <div className='menu-item px-5'>
                <Link
                  to='/institute/user-management/roles-permissions'
                  className='menu-link px-5'
                  onClick={handleRestrictedClick}
                >
                  <span className='menu-icon'>
                    <i className='fas fa-shield-alt fs-6' />
                  </span>
                  <span className='menu-title'>
                    {intl.formatMessage({id: 'MENU.USER_MANAGEMENT_ROLES_PERMISSIONS'})}
                  </span>
                </Link>
              </div>
            </>
          )}
        </>
      )}
      {!isSelectiveAiDomain && (
        <div className='menu-item px-5'>
          <Link to='/profile' className='menu-link px-5'>
            <span className='menu-text'>{intl.formatMessage({id: 'MENU.MY_PROFILE'})}</span>
          </Link>
        </div>
      )}
      {!isSelectiveAiDomain && (isStaff() || isTeacher()) && (
        <div className='menu-item px-5'>
          <Link to={'/institute/user/mcstudent'} className='menu-link px-5'>
            <span className='menu-text'>
              {intl.formatMessage({id: 'MENU.MY_ASSIGNED_STUDENTS'})}
            </span>
          </Link>
        </div>
      )}
      {(isStudent() || isParent()) && !!generalSettings?.student_profile_front && (
        <div className='menu-item px-5'>
          <Link to={`/student/view`} className='menu-link px-5'>
            <span className='menu-text'>{intl.formatMessage({id: 'MENU.STUDENT_PROFILE'})}</span>
          </Link>
        </div>
      )}

      {!isSelectiveAiDomain && <div className='separator my-2'></div>}
      {!isSelectiveAiDomain && <Languages />}

      {/* AI Credits - mobile only */}
      {isAdmin() && shouldShowAiCredits() && (
        <>
          <div className='separator my-2 d-md-none'></div>
          <div className='menu-item px-5 d-md-none'>
            <div
              className='menu-link px-5 d-flex align-items-center justify-content-between cursor-pointer'
              onClick={handleGetAccessToken}
            >
              <div className='d-flex align-items-center'>
                <i className='fa-solid fa-coins text-warning fs-4 me-3' />
                <span className='menu-text'>
                  {intl.formatMessage({id: 'SCHEDULE.TIMETABLE.VIEW_AI_CREDITS_USAGE'})}
                </span>
              </div>
              <span className='badge badge-light-warning fw-bolder fs-7 px-2 py-1'>
                {aiCredits?.remaining_credits !== null && aiCredits?.remaining_credits !== undefined
                  ? aiCredits.remaining_credits.toLocaleString()
                  : '0'}
              </span>
            </div>
          </div>
        </>
      )}

      <div className='separator my-2'></div>

      <div className='menu-item px-5'>
        <Link
          className={clsx('menu-link px-5', isSelectiveAiDomain && 'd-flex align-items-center')}
          to='/logout'
        >
          {isSelectiveAiDomain && <i className='fa fa-sign-out fs-6 me-4' />}
          <span className='menu-text'>{intl.formatMessage({id: 'MENU.SIGN_OUT'})}</span>
        </Link>
      </div>
    </div>
  )
}

export {HeaderUserMenu}
